import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import styles from './styles/base.module.css';
/**
 * `<Tab />` Вкладки организуют контент на разных экранах, наборах данных и других взаимодействиях. Вкладки организуют и позволяют перемещаться между группами контента, которые связаны, и на том же уровне иерархии.
 *
 * @example ``` tsx
 *  <Tab>Text</Tab>
 * ```
 */
export const Tab = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    tabIndex,
    className,
    children,
    active,
    iconLeft,
    iconRight,
    onClick,
    onFocus,
    disabled = false,
    size = 'l',
    ...otherProps
  } = props;
  const classNames = useMemo(() => clsx(styles.tab, className, styles[size], {
    [styles.active]: active,
    [styles.disabled]: disabled
  }), [className, size, active, disabled]);
  const tabIconLeft = useMemo(() => clsx(styles.tabIcon, styles.tabIconFirst), []);
  const tabIconRight = useMemo(() => clsx(styles.tabIcon, styles.tabIconLast), []);
  const index = useMemo(() => {
    if (typeof tabIndex !== 'undefined') {
      return tabIndex;
    }
    return active ? 0 : -1;
  }, [tabIndex, active]);
  const onClickTab = useCallback(e => {
    if (e) {
      e.preventDefault();
    }
    if (onClick && !disabled) {
      onClick(e, index);
    }
  }, [onClick, index, disabled]);
  const onFocusTab = useCallback(e => {
    if (onFocus && !disabled) {
      onFocus(e, index);
    }
  }, [onFocus, index, disabled]);
  return /*#__PURE__*/React.createElement("button", {
    disabled: disabled,
    role: "tab",
    type: "button",
    ref: ref,
    onClick: onClickTab,
    onFocus: onFocusTab,
    className: classNames,
    "aria-selected": active,
    tabIndex: active ? 0 : -1,
    ...otherProps
  }, iconLeft && /*#__PURE__*/React.createElement("span", {
    className: tabIconLeft
  }, iconLeft), children, iconRight && /*#__PURE__*/React.createElement("span", {
    className: tabIconRight
  }, iconRight));
});